import { Close } from '@mui/icons-material'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { FormikProvider, useFormik } from 'formik'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AltGrowthButton from '~/components/altGrowthButton'
import CampaignTemplateOwnerType from '~/features/admin/components/adminCampaignTemplatesCreate/campaignTemplateOwnerType'
import CampaignTemplatesSearchKeywords from '~/features/admin/components/adminCampaignTemplatesCreate/campaignTemplatesSearchKeywords'
import CampaignTemplatesVerticalTags from '~/features/admin/components/adminCampaignTemplatesCreate/campaignTemplatesVerticalTags'
import CampaignTemplateVisibility from '~/features/admin/components/adminCampaignTemplatesCreate/campaignTemplateVisibility'
import {
  createTemplateFromCampaignSchema,
  ECampaignTemplateFormKeys,
  TCreateCampaignTemplatePayload,
  TCreateTemplateFromCampaignForm,
  TCreateTemplateFromCampaignPayload,
} from '~/features/admin/models'
import useCreateTemplateFromCampaign from '~/features/campaigns/hooks/useCreateTemplateFromCampaign'
import useIsAdmin from '~/features/campaigns/hooks/useIsAdmin'
import {
  ECampaignTemplateOwnerType,
  ECampaignTemplateVisibility,
} from '~/features/campaigns/models'
import { CREATE_CAMPAIGN_DEFAULT_ID } from '~/features/campaigns/utils/constants'
import { EAdminPortalClientPath } from '~/models/api'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

export default function OFUCreateTemplateFromCampaign() {
  const isAdmin = useIsAdmin()
  const [isOpen, setIsOpen] = useState(false)
  const { id: campaignId } = useParams()
  const navigate = useNavigate()

  const { mutateAsync: createTemplateFromCampaign, isPending } =
    useCreateTemplateFromCampaign()

  const handleNavigateToTemplate = (templateId: string) => {
    navigate(
      pathBuilder(EAdminPortalClientPath.campaignTemplatesCreateEdit, {
        templateId,
      }),
    )
  }

  const handleCreateTemplate = async (
    payload: TCreateTemplateFromCampaignPayload,
  ) => {
    await createTemplateFromCampaign(
      { payload },
      {
        onSuccess: (res) => {
          if (res?.id) {
            handleNavigateToTemplate(res.id)
          }
        },
      },
    )
  }

  const formik = useFormik<TCreateTemplateFromCampaignForm>({
    initialValues: {
      [ECampaignTemplateFormKeys.friendlyName]: '',
      [ECampaignTemplateFormKeys.ownerType]: ECampaignTemplateOwnerType.system,
      [ECampaignTemplateFormKeys.visibility]:
        ECampaignTemplateVisibility.global,
      [ECampaignTemplateFormKeys.displayName]: '',
      [ECampaignTemplateFormKeys.description]: '',
      [ECampaignTemplateFormKeys.verticalTags]: [],
      [ECampaignTemplateFormKeys.searchKeywords]: [],
    },
    validationSchema: createTemplateFromCampaignSchema,
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      if (!campaignId || campaignId === CREATE_CAMPAIGN_DEFAULT_ID) {
        toast.error('Campaign ID is required')
        return
      }
      const { friendlyName, ownerType, visibility, ...initialVersionPayload } =
        values

      const templatePayload: TCreateCampaignTemplatePayload = {
        friendlyName,
        ownerType,
        visibility,
      }

      const payload: TCreateTemplateFromCampaignPayload = {
        template: templatePayload,
        initialVersion: initialVersionPayload,
        campaignId,
      }

      await handleCreateTemplate(payload)
      formik.resetForm()
      setIsOpen(false)
    },
  })

  const { values, errors, handleChange, handleSubmit } = formik

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = () => {
    formik.resetForm()
    setIsOpen(false)
  }

  if (!isAdmin) {
    return null
  }
  return (
    <>
      <AltGrowthButton
        onClick={handleOpen}
        disabled={false}
        color="primary"
        customSx={{ width: '180px' }}
      >
        Create Template
      </AltGrowthButton>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {
            p: 2,
            width: '100%',
            maxWidth: rem(800),
            borderRadius: rem(10),
            maxHeight: rem(600),
            overflowY: 'auto',
          },
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <Typography variant="h6">Create Template</Typography>
              <Typography variant="body2" color="text.secondary">
                Create a new template from this campaign.
              </Typography>
            </Stack>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ width: '100%' }}>
          <Stack gap={2} sx={{ width: '100%', mt: 2 }}>
            <Typography variant="body1" fontWeight={600}>
              Basic Information
            </Typography>
            <Stack gap={2} direction="row">
              <TextField
                fullWidth
                size="small"
                required
                label="Template Friendly Name (internal)"
                name={ECampaignTemplateFormKeys.friendlyName}
                value={values[ECampaignTemplateFormKeys.friendlyName]}
                onChange={handleChange}
                error={Boolean(errors[ECampaignTemplateFormKeys.friendlyName])}
                helperText={errors[ECampaignTemplateFormKeys.friendlyName]}
                disabled={isPending}
              />

              <TextField
                fullWidth
                size="small"
                required
                label="Display Name"
                name={ECampaignTemplateFormKeys.displayName}
                value={values[ECampaignTemplateFormKeys.displayName]}
                onChange={handleChange}
                error={Boolean(errors[ECampaignTemplateFormKeys.displayName])}
                helperText={errors[ECampaignTemplateFormKeys.displayName]}
                disabled={isPending}
              />
            </Stack>
            <TextField
              size="small"
              label="Description"
              name={ECampaignTemplateFormKeys.description}
              value={values[ECampaignTemplateFormKeys.description]}
              onChange={handleChange}
              error={Boolean(errors[ECampaignTemplateFormKeys.description])}
              helperText={errors[ECampaignTemplateFormKeys.description]}
              multiline
              minRows={3}
              maxRows={4}
              disabled={isPending}
            />
            <FormikProvider value={formik}>
              <Stack direction="row" spacing={2}>
                <CampaignTemplateOwnerType isLoading={isPending} />
                <CampaignTemplateVisibility isLoading={isPending} />
              </Stack>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 2 }}>
                Search & Organization
              </Typography>

              <CampaignTemplatesSearchKeywords isLoading={isPending} />
              <CampaignTemplatesVerticalTags isLoading={isPending} />
            </FormikProvider>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ gap: 2 }}>
          <AltGrowthButton
            onClick={handleClose}
            color="secondary"
            disabled={isPending}
          >
            Cancel
          </AltGrowthButton>
          <AltGrowthButton
            onClick={() => handleSubmit()}
            color="primary"
            disabled={isPending}
          >
            {isPending && <CircularProgress size={20} sx={{ mr: 2 }} />}
            Create Template
          </AltGrowthButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
